import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarSearch, Handshake, MapPin, Users } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useLawyerMeetings } from '../hooks/useLawyerMeetings';
import { ShowMoreFooter, LIST_PAGE_SIZE } from './ShowMoreFooter';

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = LIST_PAGE_SIZE;

// Matches the calendar feature's event-target palette (EVENT_COLORS).
const TARGET_BADGE: Record<string, string> = {
  FIRM_EVENT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  NEW_CLIENT: 'bg-sky-50 text-sky-700 border-sky-200',
  CASE_MEETING: 'bg-violet-50 text-violet-700 border-violet-200',
};

function daysUntil(iso: string): number {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const start = new Date(iso);
  start.setHours(0, 0, 0, 0);
  return Math.round((start.getTime() - startOfToday.getTime()) / DAY_MS);
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleString('en-US', { month: 'short' })} ${String(d.getDate()).padStart(2, '0')}`;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function WhenChip({ startsAt }: { startsAt: string }) {
  const { t } = useTranslation('common');
  const days = daysUntil(startsAt);
  const cls =
    days === 0
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : days <= 3
        ? 'bg-sky-50 text-sky-700 border-sky-200'
        : 'bg-green-50 text-green-700 border-green-100';
  const label =
    days === 0
      ? t('dashboard.dueToday')
      : days === 1
        ? t('dashboard.dueTomorrow')
        : t('dashboard.meetingInDays', { days });
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[0.6875rem] font-medium border whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
}

export function MyMeetingsCard() {
  const { t } = useTranslation('common');
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Upcoming only, soonest first — paging runs server-side, +10 per click.
  const { data: meetings, isLoading } = useLawyerMeetings({ limit });
  const rows = meetings?.data ?? [];
  const hiddenCount = Math.max(0, (meetings?.total ?? 0) - rows.length);

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <Handshake size={16} className="text-slate-400" /> {t('dashboard.meetings')}
        </CardTitle>
        <CardDescription className="mt-1.5">{t('dashboard.meetingsLawyerDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-2 py-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400">
            <CalendarSearch size={28} className="mb-2 text-slate-300" />
            <span className="text-sm">{t('dashboard.noUpcomingMeetings')}</span>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="w-10 px-2 py-2.5" />
                  {[
                    t('date'),
                    t('dashboard.topic'),
                    t('dashboard.eventTypeCol'),
                    t('dashboard.matter'),
                    t('dashboard.locationCol'),
                  ].map(h => (
                    <th key={h} className="text-left px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-2 py-2">
                      <Link
                        to={`/app/calendar?date=${row.startsAt?.slice(0, 10)}&view=day`}
                        title={t('dashboard.openInCalendar')}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 shadow-xs transition-colors hover:border-[#0069D1] hover:text-[#0069D1]"
                      >
                        <CalendarSearch size={14} />
                      </Link>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-semibold text-slate-900">{formatDate(row.startsAt)}</div>
                          <div className="text-slate-400 text-[0.75rem] tabular-nums">
                            {formatTime(row.startsAt)}–{formatTime(row.endsAt)}
                          </div>
                        </div>
                        <WhenChip startsAt={row.startsAt} />
                      </div>
                    </td>
                    <td className="px-2 py-2 text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        {row?.description ?? '—'}
                        {row.attendeeCount > 1 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[0.6875rem] text-slate-400"
                            title={t('dashboard.attendeesCount', { count: row.attendeeCount })}
                          >
                            <Users size={11} />
                            {row.attendeeCount}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.75rem] font-medium border ${TARGET_BADGE[row.target] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {t(`target.${row.target}`)}
                      </span>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {row?.matterId != null ? (
                        <>
                          <Link to={`/app/matters/${row.matterId}`} className="font-semibold text-[#0069D1] hover:underline">
                            {row?.matterName ?? '—'}
                          </Link>
                          {row?.clientId != null && (
                            <Link to={`/app/clients/${row.clientId}`} className="block text-[0.75rem] text-slate-500 hover:underline">
                              {row?.clientName ?? ''}
                            </Link>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 max-w-55">
                      {row?.location ? (
                        <span className="flex items-center gap-1 text-slate-600">
                          <MapPin size={12} className="shrink-0 text-slate-400" />
                          <span className="min-w-0 truncate" title={row.location}>{row.location}</span>
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ShowMoreFooter
              hiddenCount={hiddenCount}
              visibleCount={rows.length}
              onShowMore={() => setLimit(l => l + PAGE_SIZE)}
              onShowLess={() => setLimit(PAGE_SIZE)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
